import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AiManagedObjectLoadStatus,
	AgentManagedExtensionTemplateConfig,
} from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import { getAiManagedObjectWithCache } from '../ai-managed-object-cache';
import {
	type ManagedBlockConfiguration,
	getPanelConfigurationFromAimoConfig,
	getPanelConfigurationFromTemplateConfig,
} from './configuration';

type UseAiManagedObjectConfigurationLoadArgs = {
	aimoId?: string;
	aimoLoadStatus?: AiManagedObjectLoadStatus;
	cachedAimo?: AiManagedObject;
	configLocalId?: string;
	editorView: EditorView;
	foundPos?: number;
	isOpen: boolean;
	templateConfig?: AgentManagedExtensionTemplateConfig;
};

type UseAiManagedObjectConfigurationLoadResult = {
	aimoData: AiManagedObject | null;
	configurationLoadError: string | null;
	handleRetryConfigurationLoad: () => void;
	initialConfiguration: ManagedBlockConfiguration;
	isLoadingConfiguration: boolean;
	setAimoData: (aimoData: AiManagedObject | null) => void;
};

export const useAiManagedObjectConfigurationLoad = ({
	aimoId,
	aimoLoadStatus,
	cachedAimo,
	configLocalId,
	editorView,
	foundPos,
	isOpen,
	templateConfig,
}: UseAiManagedObjectConfigurationLoadArgs): UseAiManagedObjectConfigurationLoadResult => {
	const intl = useIntl();
	const configurationLoadErrorMessage = intl.formatMessage(messages.failedToLoadConfiguration);
	const [aimoData, setAimoData] = useState<AiManagedObject | null>(cachedAimo ?? null);
	const loadRequestIdRef = useRef(0);
	const isMountedRef = useRef(true);
	const isLoadedAimoCurrent = Boolean(aimoId && aimoData?.id === aimoId);
	const isExistingConfigurationPending =
		isOpen &&
		foundPos !== undefined &&
		Boolean(aimoId) &&
		!cachedAimo &&
		!isLoadedAimoCurrent &&
		aimoLoadStatus !== 'failed';

	const cancelPendingConfigurationLoad = useCallback(() => {
		loadRequestIdRef.current += 1;
	}, []);

	const loadAiManagedObject = useCallback(() => {
		if (!aimoId) {
			return;
		}

		const requestId = loadRequestIdRef.current + 1;
		loadRequestIdRef.current = requestId;
		void getAiManagedObjectWithCache({ aimoId, cachedAimo, editorView, localId: configLocalId })
			.then((aimo) => {
				if (!isMountedRef.current || loadRequestIdRef.current !== requestId) {
					return;
				}
				setAimoData(aimo);
			})
			.catch(() => {
				if (!isMountedRef.current || loadRequestIdRef.current !== requestId) {
					return;
				}
				setAimoData(null);
			});
	}, [aimoId, cachedAimo, configLocalId, editorView]);

	useEffect(
		() => () => {
			isMountedRef.current = false;
			cancelPendingConfigurationLoad();
		},
		[cancelPendingConfigurationLoad],
	);

	useEffect(() => {
		if (!isOpen || foundPos === undefined) {
			cancelPendingConfigurationLoad();
			setAimoData(null);
			return;
		}

		if (!aimoId) {
			cancelPendingConfigurationLoad();
			setAimoData(null);
			return;
		}

		if (cachedAimo) {
			cancelPendingConfigurationLoad();
			setAimoData(cachedAimo);
			return;
		}

		if (aimoLoadStatus !== 'failed' && aimoLoadStatus !== 'loading') {
			loadAiManagedObject();
		}
	}, [
		aimoId,
		aimoLoadStatus,
		cachedAimo,
		cancelPendingConfigurationLoad,
		foundPos,
		isOpen,
		loadAiManagedObject,
	]);

	return {
		aimoData,
		configurationLoadError: aimoLoadStatus === 'failed' ? configurationLoadErrorMessage : null,
		handleRetryConfigurationLoad: loadAiManagedObject,
		initialConfiguration: aimoData?.config
			? getPanelConfigurationFromAimoConfig(aimoData.config)
			: getPanelConfigurationFromTemplateConfig(templateConfig),
		isLoadingConfiguration: isExistingConfigurationPending || aimoLoadStatus === 'loading',
		setAimoData,
	};
};
